import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building2, Moon, Sun, Shield, Wrench, Rocket } from 'lucide-react';
import ClientArea from './ClientArea';
import CompanyArea from './CompanyArea';
import ToolsArea from './ToolsArea';
import DeployAuth from './DeployAuth';

const LandingPage = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  if (selectedArea === 'client') {
    return <ClientArea onBack={() => setSelectedArea(null)} />;
  }

  if (selectedArea === 'company') {
    return <CompanyArea onBack={() => setSelectedArea(null)} />;
  }

  if (selectedArea === 'tools') {
    return <ToolsArea onBack={() => setSelectedArea(null)} />;
  }

  if (selectedArea === 'deploy') {
    return <DeployAuth onBack={() => setSelectedArea(null)} />;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-elegant-md">
              <div className="w-6 h-6 border-2 border-primary-foreground rounded-md" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Portal Aplicații</h1>
              <p className="text-xs text-muted-foreground">Selectează o zonă pentru a continua</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggleDarkMode}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSelectedArea('deploy')}
              className="rounded-xl border-border hover:bg-orange-100 dark:hover:bg-orange-900/30 hover:border-orange-500 hover:text-orange-600 dark:hover:text-orange-400 transition-smooth"
              title="Deploy/Updates"
            >
              <Rocket className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Bine ai venit!
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Selectează zona de acces pentru aplicațiile tale profesionale.
            </p>
          </div>

          {/* Area Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto animate-scale-in">
            {/* Client Area */}
            <div
              onClick={() => setSelectedArea('client')}
              className="group relative bg-card border border-border hover:border-accent/50 rounded-2xl p-8 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-elegant-lg hover:-translate-y-1"
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-emerald-500/0 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative z-10">
                {/* Icon */}
                <div className="bg-emerald-100 dark:bg-emerald-900/30 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                </div>
                
                {/* Content */}
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300">
                    Client Area
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Acces direct pentru management clienți și coordonare șantiere.
                  </p>
                  
                  {/* Apps list */}
                  <div className="pt-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span>Management Clienți</span>
                    </div>
                  </div>
                </div>
                
                {/* Arrow indicator */}
                <div className="mt-6 flex items-center text-emerald-600 dark:text-emerald-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
                  <span>Intră în Client Area</span>
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Tools Area */}
            <div
              onClick={() => setSelectedArea('tools')}
              className="group relative bg-card border border-border hover:border-accent/50 rounded-2xl p-8 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-elegant-lg hover:-translate-y-1"
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-amber-500/0 to-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative z-10">
                {/* Icon */}
                <div className="bg-amber-100 dark:bg-amber-900/30 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Wrench className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                </div>
                
                {/* Content */}
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors duration-300">
                    Tools Area
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Unelte și utilități pentru uz general.
                  </p>
                  
                  {/* Security badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-400">
                    <Shield className="w-3.5 h-3.5" />
                    <span>Acces Securizat</span>
                  </div>
                </div>
                
                {/* Arrow indicator */}
                <div className="mt-6 flex items-center text-amber-600 dark:text-amber-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
                  <span>Intră în Tools Area</span>
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Company Area */}
            <div
              onClick={() => setSelectedArea('company')}
              className="group relative bg-card border border-border hover:border-accent/50 rounded-2xl p-8 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-elegant-lg hover:-translate-y-1"
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/0 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative z-10">
                {/* Icon */}
                <div className="bg-primary/10 dark:bg-primary/20 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-8 h-8 text-primary dark:text-accent" />
                </div>
                
                {/* Content */}
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground group-hover:text-accent transition-colors duration-300">
                    Company Area
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Zonă securizată pentru management intern al companiei.
                  </p>
                  
                  {/* Security badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-light/50 rounded-lg text-xs font-medium text-accent">
                    <Shield className="w-3.5 h-3.5" />
                    <span>Acces Securizat</span>
                  </div>
                </div>
                
                {/* Arrow indicator */}
                <div className="mt-6 flex items-center text-accent text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
                  <span>Intră în Company Area</span>
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Info Footer */}
          <div className="mt-16 text-center">
            <Card className="inline-block bg-card/50 backdrop-blur-sm border-border rounded-2xl px-8 py-4">
              <p className="text-sm text-muted-foreground">
                💡 <span className="font-medium text-foreground">Sfat:</span> Client Area este accesibilă direct, Company Area necesită autentificare.
              </p>
            </Card>
          </div>

          {/* Version */}
          <p className="text-center text-xs text-muted-foreground/50 mt-8" data-testid="app-version">v2.2.0</p>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
